import type { IOrganizationJSON, IRoleAssignmentJSON } from 'external-api-types';

export type UserOrTeamId = string;
export type RoleAssignmentType = 'user' | 'team';
export type UserOrTeamOrLabelId = string;
type EntityName = string;
type EntityId = string;
export type Role = string;
type EntityACLMap = Record<EntityName, Record<EntityId, IRoleAssignmentJSON[]>>;

export type ProjectRoleOptions = {
    locales?: string[];
};

export type OrganizationRoleOptions = {
    defaultProjectRole?: string;
};

export const TEAM_DEFAULT_PROJECT_ROLE = 'default_project_role';

export type IdName = {
    id: string;
    name?: string;
    displayName?: string;
    email?: string;
    isPending?: boolean;
    defaultRole?: string;
    memberHasDefaultRole?: boolean;
};
export type SubjectMapType = 'userById' | 'teamById' | 'projectGroupById';
export type ACLRow = {
    userId?: UserOrTeamId;
    teamId?: UserOrTeamId;
    isPending?: boolean;
    role: Role;
    options?: ProjectRoleOptions; // TODO: split options to other types (e.g. acl of project & organization has different options)
};
export type AccessControlList = ACLRow[];
export type ControlledObject = IdName & { accessControlList: AccessControlList };
export type SubjectMap = Record<SubjectMapType, Record<string, IdName>>;

export type RoleAssignmentMap = Record<UserOrTeamId, Record<EntityName, Record<EntityId, Role>>>;
function getModelAclMap(organization: IOrganizationJSON): EntityACLMap {
    const nestedControlledEntitiesKeys = Object.keys(organization).filter(
        (key) =>
            (organization as $TSFixMe)[key]?.some /* is array */ &&
            (organization as $TSFixMe)[key].some((entity: $TSFixMe) => entity && entity.accessControlList !== undefined)
    );
    const aclMap: EntityACLMap = {
        organization: { [organization.id!]: organization.accessControlList! }
    };
    nestedControlledEntitiesKeys.forEach((key) => {
        aclMap[key] = (organization as Record<string, $TSFixMe>)[key]
            ?.filter((object: $TSFixMe) => object.accessControlList)
            .reduce((acc: Record<EntityId, IRoleAssignmentJSON[]>, object: $TSFixMe) => {
                const { id, accessControlList } = object;
                return {
                    ...acc,
                    [id]: accessControlList
                };
            }, {});
    });

    return aclMap;
}

function addRoleAssignmentRecord({
    roleByUserOrTeamId,
    key,
    id,
    role,
    userId,
    teamId
}: {
    roleByUserOrTeamId: RoleAssignmentMap;
    key: string;
    id: string;
    role: string;
    userId?: string;
    teamId?: string;
}): void {
    const idStr = userId ? userId!.toString() : teamId!.toString();

    if (roleByUserOrTeamId[idStr] === undefined) {
        roleByUserOrTeamId[idStr] = {};
    }
    if (roleByUserOrTeamId[idStr]![key] === undefined) {
        roleByUserOrTeamId[idStr]![key] = {};
    }
    roleByUserOrTeamId[idStr]![key]![id] = role;
}

export function deriveRolesAssigned(organization: IOrganizationJSON): { roleByUserOrTeamId: RoleAssignmentMap } {
    const roleByUserOrTeamId: RoleAssignmentMap = {};

    const modelAclMap = getModelAclMap(organization);
    Object.keys(modelAclMap).forEach((entityName) => {
        // forEach entity name / type
        const EntityIdToACLMap = modelAclMap[entityName];
        Object.keys(EntityIdToACLMap!).forEach((entityId) => {
            // forEach entityId
            const accessControlList = EntityIdToACLMap![entityId];
            accessControlList?.forEach(({ role, userId, teamId }: IRoleAssignmentJSON) => {
                // forEach ACLRow
                addRoleAssignmentRecord({ roleByUserOrTeamId, key: entityName, id: entityId, role, userId: userId?.toString(), teamId: teamId?.toString() });
            });
        });
    });

    return { roleByUserOrTeamId };
}

export function getSubjectRole({
    roleByUserOrTeamId,
    key,
    id,
    subjectId
}: {
    roleByUserOrTeamId: RoleAssignmentMap;
    key: string;
    id: string;
    subjectId: string;
}): string | undefined {
    return roleByUserOrTeamId[subjectId]?.[key]?.[id];
}

export function isOrgAdmin({
    roleByUserOrTeamId,
    key,
    id,
    subjectId
}: {
    roleByUserOrTeamId: RoleAssignmentMap;
    key: string;
    id: string;
    subjectId: string;
}): boolean {
    const roleId = getSubjectRole({ roleByUserOrTeamId, key, id, subjectId });
    return !!roleId?.includes('admin');
}

export function hasOrgEditAccess(permissions: string[]) {
    const EDIT_PERMISSIONS = ['EDIT_MEMBERSHIP', 'EDIT_ROLE'];
    return permissions.some((permission) => EDIT_PERMISSIONS.includes(permission));
}
